import { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  text: string;
  children?: React.ReactNode;
}

export default function Tooltip({ text, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  // Dismiss on click outside
  useEffect(() => {
    if (!visible) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    }
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [visible]);

  const trigger = children ?? (
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-warm-200 text-warm-500 text-[10px] font-bold cursor-help">
      i
    </span>
  );

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={() => setVisible((v) => !v)}
    >
      {trigger}
      {visible && (
        <span className="absolute bottom-full left-0 mb-2 z-50 pointer-events-none">
          <span className="block min-w-[200px] max-w-[280px] px-3 py-2 rounded-lg bg-warm-800 text-white text-xs leading-relaxed whitespace-normal break-words shadow-lg">
            {text}
          </span>
          <span className="block w-0 h-0 ml-3 border-x-[6px] border-x-transparent border-t-[6px] border-t-warm-800" />
        </span>
      )}
    </span>
  );
}
